import type { RiskLimitField, RiskLimits } from '../limits';
import { EMPTY_RISK_LIMITS } from '../limits';
import type { AdvancedRuleFilter } from '../rules';
import type { RiskParameter } from '@thinkalpha/locates-ws-client';

export type LabRiskSettingsTab =
    | 'firm-defaults'
    | 'symbol-overrides'
    | 'provider-overrides'
    | 'account-overrides'
    | 'advanced-settings';

export interface FirmDefaultsDraftField {
    /** Unchecked rows keep their value but save as "not configured". */
    readonly enabled: boolean;
    /** `null` while the masked number input is empty. */
    readonly value: number | null;
}

export type FirmDefaultsDraft = Readonly<Record<RiskLimitField, FirmDefaultsDraftField>>;

/** Which kind of override rule the shared rule dialog is editing. */
export type RiskRuleScope = 'symbol' | 'provider' | 'account';

export interface RiskRuleDraft {
    /** Provider or account id for scoped rules; `''` for firm-level symbol rules. */
    readonly scopeId: string;
    /** Picked symbols, one rule per symbol; empty means "all symbols" for scoped rules. */
    readonly symbols: readonly string[];
    /** Draft limits; `null` means "inherit from firm defaults". */
    readonly maxPrice: number | null;
    readonly maxSize: number | null;
    readonly maxOrderTotal: number | null;
}

export interface RiskRuleDialogState {
    readonly scope: RiskRuleScope;
    readonly mode: 'create' | 'edit';
    /** Server id of the parameter being edited; `null` in create mode. */
    readonly editingId: string | null;
    readonly draft: RiskRuleDraft;
    /** True while the create/update request is in flight. */
    readonly isSaving: boolean;
    /** Message of the last failed save; cleared when a new save starts. */
    readonly error: string | null;
}

/** A rule the user asked to delete but has not confirmed yet. */
export interface PendingRuleDeletionState {
    readonly parameterId: string;
    /**
     * Identifies this confirmation, so a completion from an abandoned
     * confirmation of the same rule can't clear a newer one.
     */
    readonly token: number;
    /** True while the DELETE request is in flight. */
    readonly isDeleting: boolean;
}

export interface LabRiskSettingsState {
    /** Firm scope; `undefined` in superuser "all firms" mode. */
    readonly firmId: string | undefined;
    readonly activeTab: LabRiskSettingsTab;
    /** Last known server firm defaults; the draft's baseline. */
    readonly savedDefaults: RiskLimits;
    readonly draftDefaults: FirmDefaultsDraft;
    /** True while the firm defaults PUT is in flight. */
    readonly isSavingDefaults: boolean;
    readonly advancedFilter: AdvancedRuleFilter;
    readonly advancedSearchInput: string;
    /** Shared add/edit rule dialog; `null` when closed. */
    readonly ruleDialog: RiskRuleDialogState | null;
    /** Delete confirmation dialog; `null` when closed. */
    readonly pendingRuleDeletion: PendingRuleDeletionState | null;
}

export function draftFromDefaults(defaults: RiskLimits): FirmDefaultsDraft {
    const field = (value: number | null): FirmDefaultsDraftField => ({ enabled: value !== null, value });
    return {
        maxPrice: field(defaults.maxPrice),
        maxSize: field(defaults.maxSize),
        maxOrderTotal: field(defaults.maxOrderTotal),
    };
}

export interface FirmDefaultsDraftParse {
    readonly limits: RiskLimits;
    /** True when an enabled row's value is present but not finite and positive. */
    readonly hasInvalidInput: boolean;
}

export function parseFirmDefaultsDraft(draft: FirmDefaultsDraft): FirmDefaultsDraftParse {
    let hasInvalidInput = false;
    const parseField = (field: FirmDefaultsDraftField): number | null => {
        if (!field.enabled || field.value === null) return null;
        if (!Number.isFinite(field.value) || field.value <= 0) {
            hasInvalidInput = true;
            return null;
        }
        return field.value;
    };
    return {
        limits: {
            maxPrice: parseField(draft.maxPrice),
            maxSize: parseField(draft.maxSize),
            maxOrderTotal: parseField(draft.maxOrderTotal),
        },
        hasInvalidInput,
    };
}

export const EMPTY_RULE_DRAFT: RiskRuleDraft = {
    scopeId: '',
    symbols: [],
    maxPrice: null,
    maxSize: null,
    maxOrderTotal: null,
};

/** The parsed, still scope-less shape of a valid rule draft. Empty `symbols` means "all symbols". */
export interface ParsedRuleDraft extends RiskLimits {
    readonly symbols: readonly string[];
}

/**
 * Parse the rule dialog draft; `null` when any symbol is blank or
 * contains a `*`, or any set limit is not a finite positive number.
 * `null` limits mean "inherit". Symbols are upper-cased and deduped; an
 * empty list parses as the all-symbols wildcard.
 */
export function parseRiskRuleDraft(draft: RiskRuleDraft): ParsedRuleDraft | null {
    const symbols = draft.symbols.map((symbol) => symbol.trim().toUpperCase());
    if (symbols.some((symbol) => symbol === '' || symbol.includes('*'))) return null;
    const limits = [draft.maxPrice, draft.maxSize, draft.maxOrderTotal];
    if (limits.some((value) => value !== null && (!Number.isFinite(value) || value <= 0))) return null;
    return {
        symbols: symbols.values().unique().toArray(),
        maxPrice: limits[0],
        maxSize: limits[1],
        maxOrderTotal: limits[2],
    };
}

/** Like {@link parseRiskRuleDraft} but also requires a scope (provider/account) selection. */
export function parseScopedRuleDraft(draft: RiskRuleDraft): (ParsedRuleDraft & { scopeId: string }) | null {
    const base = parseRiskRuleDraft(draft);
    if (base === null || draft.scopeId === '') return null;
    return { scopeId: draft.scopeId, ...base };
}

/**
 * Drafted symbols that already have a stored rule for the same target —
 * the backend enforces one rule per firm/provider/account/symbol, so
 * saving these would fail. `null` marks the all-symbols rule (an empty
 * scoped draft). The rule being edited doesn't conflict with itself.
 */
export function findRuleSymbolConflicts(
    parameters: readonly RiskParameter[],
    dialog: Pick<RiskRuleDialogState, 'scope' | 'editingId' | 'draft'>,
): ReadonlyArray<string | null> {
    const { scope, editingId, draft } = dialog;
    if (scope !== 'symbol' && draft.scopeId === '') return [];
    const accountId = scope === 'account' ? draft.scopeId : null;
    const providerId = scope === 'provider' ? draft.scopeId : null;
    const existing = new Set(
        parameters
            .filter(
                (parameter) =>
                    parameter.id !== editingId &&
                    parameter.accountId === accountId &&
                    parameter.providerId === providerId,
            )
            .map((parameter) => parameter.symbol),
    );
    const drafted: ReadonlyArray<string | null> =
        draft.symbols.length === 0
            ? scope === 'symbol'
                ? []
                : [null]
            : draft.symbols.map((symbol) => symbol.trim().toUpperCase());
    return drafted.filter((symbol) => existing.has(symbol));
}

export const initialLabRiskSettingsState: LabRiskSettingsState = {
    firmId: undefined,
    activeTab: 'firm-defaults',
    savedDefaults: EMPTY_RISK_LIMITS,
    draftDefaults: draftFromDefaults(EMPTY_RISK_LIMITS),
    isSavingDefaults: false,
    advancedFilter: 'all',
    advancedSearchInput: '',
    ruleDialog: null,
    pendingRuleDeletion: null,
};
